import { filter, fx, head, isEmpty, keys, last, pipe } from '@fxts/core';
import { Alert } from '@services/MarppleShop/renderApp/components/cells/Alert/Alert.ts';
import { ValidateFormErrorKeyTypes } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/helper.ts';
import {
  errorMessage,
  formKeyMap,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import {
  EditorApiError,
  EditorSaveClientError,
  safeErrorParse,
} from '@services/MarppleShopStudio/widget/CreatorProduct/api';
import { ConfigSentryF } from '@modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { unEscaper } from '@shared/utils/escape.ts';
import { MESSAGES } from '../constants.ts';
import { BaseForm } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/BaseForm.ts';
import { CreatorProductData } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';

/**
 * 에러 처리를 담당하는 서비스 클래스
 */
export class ErrorHandler<T extends CreatorProductData = CreatorProductData> {
  constructor(private form: BaseForm<T>) {}

  /**
   * 저장 에러 처리
   */
  handleSaveError(error: unknown): void {
    console.log('error: ', error);
    if (EditorSaveClientError.is(error)) {
      error.keys.forEach((key) => this.handleValidationError(key));
      return;
    }

    const data = safeErrorParse(error, (error) => {
      error.name = 'PBG Editor BaseForm safeErrorParse onError';
      ConfigSentryF.error(error);
      Alert.open({ title: '저장하기', message: MESSAGES.SAVE.FAILED });
    });

    if (data?.message === ValidateFormErrorKeyTypes.DUPLICATED_NICKNAME) {
      this.handleValidationError(ValidateFormErrorKeyTypes.DUPLICATED_NICKNAME);
    } else {
      this.handleUnexpectedError(data);
    }
  }

  /**
   * 검증 에러 처리
   */
  async handleValidationError(error: ValidateFormErrorKeyTypes): Promise<void> {
    const errorHandlers: Record<Partial<ValidateFormErrorKeyTypes>, () => void> = {
      [ValidateFormErrorKeyTypes.MORE_THAN_SELL_END_AT]: () =>
        Alert.open({ title: '판매 종료일시', message: errorMessage.more_than_sell_end_at }),

      [ValidateFormErrorKeyTypes.DUPLICATED_NICKNAME]: () =>
        Alert.open({ title: '재고관리 번호', message: errorMessage.duplicated_sku }),

      [ValidateFormErrorKeyTypes.EMPTY_THUMBNAIL]: () =>
        Alert.open({
          title: MESSAGES.VALIDATION.EMPTY_THUMBNAIL_TITLE,
          message: MESSAGES.VALIDATION.EMPTY_THUMBNAIL_MESSAGE,
        }),

      [ValidateFormErrorKeyTypes.NOT_QNA_SNS_LINK]: () => {
        this.form.store.actions.setError(formKeyMap.qna_link, errorMessage.invalid_qna_sns_link);
        this.focusFirstError();
      },

      [ValidateFormErrorKeyTypes.NOT_MATERIAL]: () => {
        this.form.store.actions.setError(formKeyMap.material, errorMessage.required_material);
        this.focusFirstError();
      },

      [ValidateFormErrorKeyTypes.NOT_KC_CERTIFICATION]: () => {
        this.form.store.actions.setError(formKeyMap.certification_info, errorMessage.not_kc_certification);
        this.focusFirstError();
      },

      [ValidateFormErrorKeyTypes.MORE_THAN_GOAL_AMOUNT]: () => {
        this.form.store.actions.setError(formKeyMap.target_amount, errorMessage.more_than_goal_amount);
        this.focusFirstError();
      },

      [ValidateFormErrorKeyTypes.NOT_GOAL_AMOUNT]: () => {
        this.form.store.actions.setError(formKeyMap.target_amount, errorMessage.not_goal_amount);
        this.focusFirstError();
      },

      [ValidateFormErrorKeyTypes.MIN_MARKETING_PRICE]: () => {
        this.form.store.actions.setError(formKeyMap.pre_discount_price, errorMessage.min_marketing_price);
        this.focusFirstError();
      },

      [ValidateFormErrorKeyTypes.NOT_ALL_CLEAR_SKU_VOLUME]: () => {
        this.form.store.actions.setError(formKeyMap.width, errorMessage.required);
        this.focusFirstError();
        Alert.open({
          title: MESSAGES.VALIDATION.VOLUME_TITLE,
          message: MESSAGES.VALIDATION.VOLUME_MESSAGE,
        });
      },

      [ValidateFormErrorKeyTypes.CANCELLED_SKU]: () => {},
      [ValidateFormErrorKeyTypes.PUBLIC_SKU]: () => {},
      [ValidateFormErrorKeyTypes.NOT_PUBLIC_SKU]: () => {},
      [ValidateFormErrorKeyTypes.NOT_ALL_CLEAR_SKU]: () => {},
      [ValidateFormErrorKeyTypes.ALL_CLEAR_SKU_PUBLIC_SKU]: () => {},
      [ValidateFormErrorKeyTypes.CLOSE_MODAL]: () => {},
    };

    const handler = errorHandlers[error];
    if (handler) {
      handler();
    }
  }

  /**
   * 예상치 못한 에러 처리
   */
  private async handleUnexpectedError(data: Partial<EditorApiError>): Promise<void> {
    if (data?.title && data?.message) {
      await Alert.open({ title: data.title, message: unEscaper(data.message) });
      return;
    }

    if (data?.message?.includes?.('9,999,999')) {
      await Alert.open({
        title: MESSAGES.ERROR.STOCK_EXCEEDED_TITLE,
        message: data.message,
      });
      return;
    }

    if (data?.message?.includes?.('옵션 추가 금액')) {
      await Alert.open({
        title: MESSAGES.ERROR.OPTION_PRICE_EXCEEDED_TITLE,
        message: data.message,
      });
      return;
    }

    await Alert.open({
      title: MESSAGES.ERROR.UNEXPECTED_TITLE,
      message: data?.message || '500',
    });
  }

  /**
   * 첫 번째 에러 필드에 포커스
   */
  focusFirstError(): void {
    if (isEmpty(this.form.store.errors)) return;

    const selector = fx(keys(this.form.store.errors))
      .map((error_key) => last(error_key.split('.')))
      .filter((name) => Boolean(name))
      .map((name) => `[name="${name}"]`)
      .reduce((a, b) => `${a}, ${b}`);

    pipe(
      this.form.element().querySelectorAll<HTMLElement>(selector),
      filter((element) => getComputedStyle(element).display !== 'none'),
      head,
      (element) => element?.focus?.(),
    );
  }
}
